import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import toast from 'react-hot-toast';
import TemplatePreview from './components/TemplatePreview';

const INVOICE_TEMPLATES = [
  { id: 'a4', name: 'A4 Size Papper', description: 'Full A4 size with header and footer' },
  { id: 'a4-no-header', name: 'A4 Size Papper without header', description: 'A4 size without header image' },
  { id: 'small', name: 'Small Size Papper', description: 'Compact invoice for quick printing' },
  { id: 'small-no-header', name: 'Small Size Papper without header', description: 'Compact without header' },
  { id: 'customer-copy', name: 'Customer Copy & Lab Copy', description: 'Customer-facing format' },
  { id: 'header-upload', name: 'Header Image Upload', description: 'Upload custom header image' },
  { id: 'theme-7', name: 'Invoice Theme - 7', description: 'Modern design with colors' },
  { id: 'theme-8', name: 'Invoice Theme - 8', description: 'Minimal clean design' },
];

export default function InvoiceTemplateSettings() {
  const { settings, loading, updateSettings } = useSettings();
  const [selectedTemplate, setSelectedTemplate] = useState('a4');
  const [printMode, setPrintMode] = useState<'pdf_viewer' | 'browser_print'>('pdf_viewer');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setSelectedTemplate(settings.invoice_template || 'a4');
      setPrintMode(settings.print_mode || 'pdf_viewer');
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);

    const result = await updateSettings({
      invoice_template: selectedTemplate,
      print_mode: printMode,
    });

    if (result.success) {
      toast.success('Invoice template updated successfully');
    } else {
      toast.error(result.error || 'Failed to update template');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Invoice Template</h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose the template that will be used for all invoice printing and PDF generation.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {INVOICE_TEMPLATES.map((template) => (
            <div key={template.id} className="flex flex-col items-center">
              <TemplatePreview
                templateId={template.id}
                selected={selectedTemplate === template.id}
                onSelect={() => setSelectedTemplate(template.id)}
              />
              <div className="mt-3 text-center">
                <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
                <p className="text-xs text-gray-600">{template.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Print Mode Selection */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Print Mode</h3>
        <p className="text-sm text-gray-600 mb-4">
          Choose how invoices are printed when clicking "Print Invoice" button.
        </p>
        
        <div className="space-y-3">
          <label className="flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderColor: printMode === 'pdf_viewer' ? '#14b8a6' : '#e5e7eb' }}>
            <input
              type="radio"
              name="print_mode"
              value="pdf_viewer"
              checked={printMode === 'pdf_viewer'}
              onChange={(e) => setPrintMode(e.target.value as 'pdf_viewer')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">PDF Viewer (Recommended)</div>
              <div className="text-sm text-gray-600 mt-1">
                Opens a new tab with Chrome's native PDF viewer. Provides pixel-perfect layout with zoom, download, and print controls. Best for consistent output across devices.
              </div>
            </div>
          </label>

          <label className="flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderColor: printMode === 'browser_print' ? '#14b8a6' : '#e5e7eb' }}>
            <input
              type="radio"
              name="print_mode"
              value="browser_print"
              checked={printMode === 'browser_print'}
              onChange={(e) => setPrintMode(e.target.value as 'browser_print')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">Browser Print</div>
              <div className="text-sm text-gray-600 mt-1">
                Uses the browser's native print dialog. Clean 1-page output with exact margins and colors. Faster but may vary slightly across browsers.
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || (selectedTemplate === settings.invoice_template && printMode === settings.print_mode)}
          className="flex items-center space-x-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Updating...' : 'Update'}</span>
        </button>
      </div>
    </div>
  );
}
